import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Collapse, Form, Radio } from 'antd';
import type { FC } from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { ApplicationFeesTooltip } from 'app/components/commons/Room/Beds/_components/ApplicationFeesTooltip';
import { RoomCapacity } from 'app/components/commons/Room/Beds/_components/RoomCapacity/RoomCapacity';
import { SelectBedByPriority } from 'app/components/commons/Room/Beds/_components/SelectBedByPriority';
import { ExtraBed } from 'app/components/pages/Extranet/commons/ExtraBed/ExtraBed';
import { useBedCategories } from 'app/hooks/data/rooms/useBedCategories';
import type { Bed, Room } from 'app/typings';
import { BABY_COT_BED_CATEGORY_ID, BED_TASK_CATEGORIES } from 'app/typings';

import './BedsBuilder.scss';

const { Panel } = Collapse;

const RULES = [{ required: true, message: 'Required' }];

export const BedsBuilder: FC<BedsBuilderProps> = ({
  fields,
  add,
  remove,
  mode = 'edit',
  values,
}) => {
  const { data: bedCategories = [] } = useBedCategories();
  const [activeKeys, setActiveKeys] = useState<string[]>(
    fields.map((field) => `${field.key}`)
  );
  const { currencyFormatter, currencyInput } = useContext(CurrencyContext);
  const refs = useRef<(HTMLElement | null)[]>([]);
  const previousFieldsLength = useRef(fields.length);

  const activeExtraBed = useCallback(() => {
    return BED_TASK_CATEGORIES.filter((category) =>
      values.beds.some((bed) => bed.categoryId === category)
    ).length;
  }, [values.beds]);

  useEffect(() => {
    const bedWasAdded = fields.length > previousFieldsLength.current;

    previousFieldsLength.current = fields.length;

    if (!bedWasAdded) {
      return;
    }

    const newBedIndex = fields.length - (1 + activeExtraBed());

    refs?.current[newBedIndex]?.scrollIntoView({
      behavior: 'smooth',
    });
    setActiveKeys((prevActiveKeys) => {
      const newKey = `${fields[newBedIndex]?.key}`;
      const index = prevActiveKeys.indexOf(newKey);

      if (index < 0) {
        return [...prevActiveKeys, newKey];
      }

      return prevActiveKeys;
    });
  }, [fields, activeExtraBed]);

  const toggleActiveKey = (newActiveKeys: string | string[]) => {
    setActiveKeys(
      Array.isArray(newActiveKeys) ? newActiveKeys : [newActiveKeys]
    );
  };

  const renderHeader = (displayIndex: number, name: number) => {
    if (displayIndex === 0) {
      return 'Bed #01';
    }

    return (
      <div
        className="bed-header"
        ref={(element) => (refs.current[displayIndex] = element)}
      >
        {`Bed #${String(displayIndex + 1).padStart(2, '0')}`}{' '}
        {mode === 'edit' && <DeleteOutlined onClick={() => remove(name)} />}
      </div>
    );
  };

  const defaultBedOrder = new Map<number, number>();

  fields.forEach((field, index) => {
    const bed = values.beds?.[index];

    if (!BED_TASK_CATEGORIES.some((task) => task === bed?.categoryId)) {
      defaultBedOrder.set(index, defaultBedOrder.size);
    }
  });

  const filteredBedCategories = bedCategories.filter(
    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    (bedCategories) =>
      !BED_TASK_CATEGORIES.some((bedTask) => bedCategories.id === bedTask)
  );

  return (
    <div className="bedsBuilder">
      <Collapse activeKey={activeKeys} onChange={toggleActiveKey}>
        {fields.map(({ key, name, ...restField }, index) => {
          const displayIndex = defaultBedOrder.get(index);

          if (displayIndex === undefined) {
            return null;
          }

          return (
            <Panel header={renderHeader(displayIndex, name)} key={key}>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) =>
                  mode === 'edit' ? (
                    <>
                      <div className="input-row bedType">
                        <SelectBedByPriority
                          index={index}
                          getFieldValue={getFieldValue}
                          name={[name, 'categoryId']}
                          label="Type"
                          rules={RULES}
                          bedCategories={bedCategories}
                          {...restField}
                        />
                      </div>
                      {
                        <RoomCapacity
                          capacity={
                            bedCategories.find(
                              (cat) =>
                                cat.id ===
                                getFieldValue(['beds', name, 'categoryId'])
                            )?.capacity
                          }
                        />
                      }
                    </>
                  ) : (
                    <>
                      <div className="input-row bedType">
                        <p className="bedTypeTitle">Type</p>

                        <Form.Item
                          {...restField}
                          name={[name, 'categoryId']}
                          label="Type"
                          rules={RULES}
                        >
                          <div className="view-text bedTypeForm">
                            {
                              filteredBedCategories.find(
                                (cat) =>
                                  cat.id ===
                                  getFieldValue(['beds', name, 'categoryId'])
                              )?.name
                            }
                          </div>
                        </Form.Item>
                      </div>
                    </>
                  )
                }
              </Form.Item>
              {displayIndex > 0 && (
                <>
                  <div className="input-row included bedCost">
                    <p className="bedCostTitle">Cost of bed</p>
                    <Form.Item {...restField} name={[name, 'included']} noStyle>
                      <Radio.Group
                        disabled={mode !== 'edit'}
                        className="bedCostForm"
                      >
                        <Radio value={true}>Free of charge</Radio>
                        <Radio value={false}>
                          Applicable fees
                          <ApplicationFeesTooltip />
                        </Radio>
                      </Radio.Group>
                    </Form.Item>
                  </div>
                  <Form.Item shouldUpdate noStyle>
                    {({ getFieldValue }) => {
                      const display =
                        getFieldValue(['beds', name, 'included']) === false;

                      if (!display) {
                        return null;
                      }

                      return mode === 'edit' ? (
                        <div className="input-row bedPrice">
                          <p className="bedPriceTitle">Fees amount</p>
                          <div className="bedPriceForm">
                            <Form.Item
                              {...restField}
                              name={[name, 'price']}
                              rules={RULES}
                            >
                              {currencyInput({ min: 0 })}
                            </Form.Item>
                          </div>
                        </div>
                      ) : (
                        <div className="input-row bedPrice">
                          <p className="bedPriceTitle">Fees amount</p>
                          <div className="bedPriceForm">
                            <Form.Item
                              {...restField}
                              name={[name, 'price']}
                              rules={RULES}
                            >
                              <div className="view-text">{`${currencyFormatter(
                                getFieldValue(['beds', name, 'price']) || 0
                              )}`}</div>
                            </Form.Item>
                          </div>
                        </div>
                      );
                    }}
                  </Form.Item>
                </>
              )}
            </Panel>
          );
        })}

        {BED_TASK_CATEGORIES.map((bedTypeId, index) => {
          return (
            <ExtraBed
              key={'key' + index}
              listBed={values.beds}
              bed={values.beds.find((bed: Bed) => bed.categoryId === bedTypeId)}
              categoryName={
                bedTypeId === BABY_COT_BED_CATEGORY_ID
                  ? 'Baby cot'
                  : 'Guest bed'
              }
              categoryId={bedTypeId}
              add={add}
              remove={remove}
              previousPrefixes={['beds']}
              prefix={[
                values.beds.findIndex((bed) => bed.categoryId === bedTypeId),
              ]}
              bedCategories={bedCategories}
              mode={mode}
              switchChecked={values.beds.some(
                (bed) => bed.categoryId === bedTypeId
              )}
            />
          );
        })}
      </Collapse>
      <Form.Item>
        <div className="footer">
          <Button
            icon={<PlusOutlined />}
            onClick={() =>
              add({ included: true }, fields.length - activeExtraBed())
            }
          >
            Add bed
          </Button>
        </div>
      </Form.Item>
    </div>
  );
};

type FieldData = {
  name: number;
  key: number;
};

type BedsBuilderProps = {
  fields: FieldData[];
  add: (
    bed: Omit<Bed, 'included'> & { included: boolean },
    index?: number
  ) => void;
  remove: (index: number) => void;
  mode?: 'view' | 'edit';
  values: Room;
};
